'use client';

import * as React from 'react';
import { Dashboard } from '@/shared/i18n';
import { ExploreMore } from '../ui/shared';
import { Avatar as MuiAvatar, Typography, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EventBus } from '@/shared/utils';

import {
  DashboardContentsInProgress,
  DashboardFavourites,
  TrendingSeries,
  SeenContents,
  UpComingLives,
} from '../ui/elements';
import { DashboardModal } from '../ui/modal/dashboard-modal';
import { DashboardEventKeys, DashboardEvents } from '../events';
import { useDashboard } from '../hooks/use-dashboard';
import { DashboardLoader } from '../ui/shared/module-loader';
import { Auth } from 'firebase/auth';

interface ProfileScreenProps {
  auth: Auth;
  dict: Dashboard;
  eventBus: EventBus<DashboardEvents>;
}

const DashboardScreen: React.FC<ProfileScreenProps> = ({ auth, dict, eventBus }) => {
  const {
    customerSubscription,
    fetchCustomerSubscription,
    fetchFavouriteLearningPath,
    fetchFavouriteLessons,
    fetchSeenContents,
    fetchTrendingSeries,
    fetchUpComingLives,
    fetchContentsInProgress,
    createFavourite,
    deleteFavourite,
    resetStore,
  } = useDashboard(dict, auth);

  React.useEffect(() => {
    eventBus.on(DashboardEventKeys.customer.view, fetchCustomerSubscription);
    eventBus.on(DashboardEventKeys.favouriteLearningPath.view, fetchFavouriteLearningPath);
    eventBus.on(DashboardEventKeys.favouriteLessons.view, fetchFavouriteLessons);
    eventBus.on(DashboardEventKeys.seentContents.view, fetchSeenContents);
    eventBus.on(DashboardEventKeys.trendingSeries.view, fetchTrendingSeries);
    eventBus.on(DashboardEventKeys.upComingLives.view, fetchUpComingLives);
    eventBus.on(DashboardEventKeys.contentsInProgress.view, fetchContentsInProgress);
    eventBus.on(DashboardEventKeys.favourites.create, createFavourite);
    eventBus.on(DashboardEventKeys.favourites.delete, deleteFavourite);

    return () => {
      resetStore();
      eventBus.off(DashboardEventKeys.customer.view, fetchCustomerSubscription);
      eventBus.off(DashboardEventKeys.favouriteLearningPath.view, fetchFavouriteLearningPath);
      eventBus.off(DashboardEventKeys.favouriteLessons.view, fetchFavouriteLessons);
      eventBus.off(DashboardEventKeys.seentContents.view, fetchSeenContents);
      eventBus.off(DashboardEventKeys.trendingSeries.view, fetchTrendingSeries);
      eventBus.off(DashboardEventKeys.upComingLives.view, fetchUpComingLives);
      eventBus.off(DashboardEventKeys.contentsInProgress.view, fetchContentsInProgress);
      eventBus.off(DashboardEventKeys.favourites.create, createFavourite);
      eventBus.off(DashboardEventKeys.favourites.delete, deleteFavourite);
    };
  }, [
    eventBus,
    fetchCustomerSubscription,
    fetchFavouriteLearningPath,
    fetchFavouriteLessons,
    fetchSeenContents,
    fetchTrendingSeries,
    fetchUpComingLives,
    fetchContentsInProgress,
    resetStore,
  ]);

  if (!customerSubscription || !customerSubscription?.data)
    return <DashboardLoader key="profile-loader" />;

  const isSubscriber = customerSubscription.data.subscriber;
  const isProfileCompleted = customerSubscription.data.isOnboardingCompleted;
  return (
    <div className="mb-10">
      <div className="flex flex-col gap-6 items-center mb-8">
        <div className="flex justify-center items-center gap-4">
          {/* Avatar with dynamic image source */}
          <MuiAvatar
            sx={{ height: '96px', width: '96px' }}
            src={customerSubscription?.data?.profilePicture || ''}
          />
          <div>
            {/* Display the username dynamically */}
            <Typography variant="h1">
              Ciao
              {customerSubscription?.data?.displayName
                ? ` ${customerSubscription?.data?.displayName?.split(' ')?.[0]}!`
                : '!'}
            </Typography>

            {/* Conditional Rendering for Profile Completion */}
            {!isProfileCompleted && (
              <div className="flex items-center gap-2">
                <InfoOutlinedIcon color="warning" />
                <Typography variant="s2" className="text-warning-light">
                  Profilo non completato
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Conditional Rendering for Onboarding Button */}
        {!isProfileCompleted && (
          <Button
            onClick={() =>
              eventBus.emit(DashboardEventKeys.redirects.to, {
                url: '/onboarding',
                isExternalPage: false,
              })
            }
            variant="contained"
            color="primary"
            size="medium"
          >
            <Typography variant="button-m">Completa onboarding</Typography>
          </Button>
        )}
      </div>
      {/* Only for NOT Subscribers */}
      {!isSubscriber && (
        <div className="mb-8">
          <ExploreMore
            eventBus={eventBus}
            isLoading={customerSubscription.status.isLoading}
            isSubscribed={isSubscriber}
            dict={dict}
            url="/abbonati"
          />
        </div>
      )}

      {/* Favourites Section */}
      <DashboardFavourites isSubscribed={isSubscriber} dict={dict} eventBus={eventBus} />

      <div className="sm:flex gap-x-6 space-y-8 w-full">
        <div className="flex flex-1 flex-col gap-10 mt-8 overflow-hidden">
          {/* Started Contents Section */}
          <DashboardContentsInProgress dict={dict} eventBus={eventBus} />
          {/* Seen Until Now Section */}
          <SeenContents dict={dict} eventBus={eventBus} />
          {/* Serie di Tendenza Section */}
          <TrendingSeries hasModal dict={dict} eventBus={eventBus} />
          {/* Live in Arrivo Section */}
          <UpComingLives isSubscriber={isSubscriber} dict={dict} eventBus={eventBus} />
        </div>
      </div>

      {/* Only for Subscribers */}
      {isSubscriber && (
        <div className="mt-8">
          <ExploreMore
            eventBus={eventBus}
            isLoading={customerSubscription.status.isLoading}
            isSubscribed={isSubscriber}
            dict={dict}
            url="/catalogo"
          />
        </div>
      )}
      <DashboardModal eventBus={eventBus} />
    </div>
  );
};

export default DashboardScreen;
